'use client';

import * as ToastPrimitive from '@radix-ui/react-toast';
import { DynamicIcon } from 'phosphor-icons/react';
import * as React from 'react';
import { useState } from 'react';
import { c } from '../../../utils/tailwind-merge';
import { Button } from '../../atoms/Button';
import { RichText } from '../RichText';
import { SnackBarProps } from './SnackBar.types';

export const SnackBar = ({
  microcopy,
  icon,
  iconWeight,
  actionIcon,
  closeIcon,
  duration = 3000,
  onClick,
  onActionClick,
  onCloseIconClick,
  onClose,
  className = '',
}: SnackBarProps) => {
  const [open, setOpen] = useState(true);

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen) {
      onClose();
    }
  };

  const handleClick = () => {
    if (onClick) {
      onClick();
    }
  };

  const handleActionTextClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.stopPropagation();
    if (onActionClick) {
      onActionClick();
    }
  };

  const handleCloseClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (onCloseIconClick) {
      onCloseIconClick();
    } else {
      onClose();
    }
    setOpen(false);
  };

  return (
    <ToastPrimitive.Provider swipeDirection="right" duration={duration}>
      <ToastPrimitive.Root
        data-slot="snack-bar"
        open={open}
        onOpenChange={handleOpenChange}
        className={c(
          'pointer-events-auto flex items-center overflow-hidden rounded-lg',
          'h-14 border border-grey-800 bg-grey-900 text-white shadow-card',
          'gap-3 pl-4 pr-0 text-base leading-6 tracking-tight',
          className
        )}
        onClick={handleClick}
      >
        {icon ? (
          <DynamicIcon
            iconName={icon}
            size={24}
            weight={iconWeight}
            aria-hidden="true"
            focusable="false"
            className="flex-shrink-0 text-white"
          />
        ) : null}
        <ToastPrimitive.Title>
          <RichText
            html={microcopy.title}
            type="span"
            className="text-base font-normal leading-6 tracking-tight text-white"
          />
        </ToastPrimitive.Title>
        {(microcopy.action || actionIcon) && (
          <ToastPrimitive.Action asChild altText={microcopy.action || 'Action'}>
            <Button.v2
              onClick={handleActionTextClick}
              variant="tertiary-blue-monochrome"
              size="xsmall"
              shape="square"
              aria-label={actionIcon ? microcopy.action : undefined}
              label={actionIcon ? undefined : microcopy.action}
              iconRight={actionIcon}
              iconClassName="text-blue !h-5 !w-5"
              className={c(
                'inline-flex items-center rounded',
                'text-base font-medium leading-6 tracking-tight text-blue',
                'border-none bg-transparent hover:bg-transparent focus:bg-transparent active:bg-transparent',
                actionIcon &&
                  'h-5 w-5 p-0 [&_svg]:m-0 [&_svg]:h-full [&_svg]:w-full'
              )}
            />
          </ToastPrimitive.Action>
        )}
        <ToastPrimitive.Close asChild>
          <Button.v2
            type="button"
            aria-label={microcopy.closeAriaLabel}
            variant="tertiary-black-monochrome"
            size="large"
            shape="square"
            onClick={handleCloseClick}
            iconRight={closeIcon}
            iconClassName="text-white !w-5 !h-5"
            className={c(
              'flex h-14 w-14 items-center justify-center',
              'rounded-none border-0 border-l border-grey-700',
              'bg-grey-900 hover:bg-grey-900 focus:bg-grey-900 active:bg-grey-900'
            )}
          />
        </ToastPrimitive.Close>
      </ToastPrimitive.Root>
      <ToastPrimitive.Viewport
        data-slot="snack-bar-viewport"
        className="fixed inset-x-0 bottom-4 z-50 flex justify-center px-4 md:bottom-5"
      />
    </ToastPrimitive.Provider>
  );
};
